/**
 * documentSoftDelete — DDL-FOLLOWUP-CONFIRM-DIALOG + DDL-FOLLOWUP-RESTORE.
 *
 * The Document Library used to HARD-delete a doc the moment the user
 * clicked the trash icon. A misclick (or a confused-user "this is the
 * wrong upload" reaction the moment after dropping a critical permit)
 * was unrecoverable: the SiteDocument record was filtered out of every
 * site / report array and the GCS bytes deleted on the next pass.
 *
 * The new contract:
 *   - Delete is now SOFT — the doc record stays in `site.documents` (and
 *     in every `report.documents` on the site, per the DDL-1 cross-array
 *     invariant) but gets stamped with `deletedAt` / `deletedBy`.
 *   - Default views (Documents tab, Coverage Matrix DCT pool, SED bootstrap)
 *     hide soft-deleted docs via `isLiveDoc(doc)`.
 *   - Soft-deleted docs auto-purge after `SOFT_DELETE_TTL_DAYS` (7) — the
 *     cron-side purge OR a one-line filter in the read path treats any
 *     doc whose `deletedAt` is older than the TTL as "as if hard-deleted".
 *   - An admin "Restore" action in the Ghost Docs panel nulls `deletedAt`
 *     and `deletedBy`, returning the doc to the live set.
 *
 * This module is the SINGLE source of truth for the TTL constant and
 * for the `isSoftDeleted` / `isLiveDoc` / `isExpiredSoftDelete` predicates
 * so callers can't drift on the comparison semantics (millisecond strict-
 * inequality, `deletedAt: null` treated identically to absent, NaN-safe
 * via `Number.isFinite` guards, etc.).
 *
 * Pure module. No React, no Firestore, no side-effects. Trivially testable.
 */

import type { SiteDocument } from '../types/siteRag';
import type { SiteRagReport } from '../types/siteRag';

/**
 * Days a soft-deleted document is restorable from the Ghost Docs panel
 * before the purge cron is allowed to hard-delete it. Pure constant —
 * any caller that wants to know "how long do I have?" reads this value,
 * never a literal `7` sprinkled in code.
 */
export const SOFT_DELETE_TTL_DAYS = 7;

/** Convenience: TTL expressed in ms. Pre-multiplied so the hot read path
 *  doesn't redo `7 * 24 * 60 * 60 * 1000` on every render. */
export const SOFT_DELETE_TTL_MS = SOFT_DELETE_TTL_DAYS * 24 * 60 * 60 * 1000;

/**
 * Parse `deletedAt` into an epoch-ms number. Returns `null` for the live
 * states (absent or explicit `null`) and for malformed strings (so the
 * caller can fail-safe to "treated as live" rather than crash on a bad
 * Firestore field). Used internally by the predicates below — exported
 * mainly so the GhostDocs surface can render a relative timestamp.
 */
export function parseSoftDeleteAt(doc: Pick<SiteDocument, 'deletedAt'>): number | null {
  const raw = doc.deletedAt;
  if (raw === null || raw === undefined) return null;
  if (typeof raw !== 'string') return null;
  const ms = Date.parse(raw);
  // Date.parse returns NaN on bad input — `Number.isFinite` guards
  // strictly so a future "Infinity" / "-Infinity" string can't trip it.
  if (!Number.isFinite(ms)) return null;
  return ms;
}

/**
 * `true` ⇔ the doc has been soft-deleted (regardless of TTL). Use this
 * when you want to surface the soft-delete state — e.g. the Ghost Docs
 * admin row. The default Documents-tab filter should use `isLiveDoc`
 * (its inverse + TTL semantics) instead.
 */
export function isSoftDeleted(doc: Pick<SiteDocument, 'deletedAt'>): boolean {
  return parseSoftDeleteAt(doc) !== null;
}

/**
 * `true` ⇔ the doc's soft-delete tombstone is older than the TTL. These
 * rows are eligible for hard-purge by the cron. The Documents-tab read
 * path also treats them as "gone" (same effect as a real hard-delete)
 * so a stale tombstone that hasn't been cron-collected yet still
 * disappears from the list.
 *
 * Math: `now - deletedAt > TTL_MS`. Strict inequality so a doc at
 * exactly the TTL is still considered restorable for one more tick —
 * matches the "you have 7 days" copy in the confirm modal.
 */
export function isExpiredSoftDelete(
  doc: Pick<SiteDocument, 'deletedAt'>,
  nowMs: number = Date.now(),
): boolean {
  const at = parseSoftDeleteAt(doc);
  if (at === null) return false;
  if (!Number.isFinite(nowMs)) return false;
  return nowMs - at > SOFT_DELETE_TTL_MS;
}

/**
 * `true` ⇔ the doc should appear in the default Documents tab + SED
 * bootstrap + Coverage Matrix DCT pool. Composes the inverse of
 * `isSoftDeleted` with `isExpiredSoftDelete` so a stale tombstone that
 * the cron hasn't reached yet still hides the doc.
 */
export function isLiveDoc(
  doc: Pick<SiteDocument, 'deletedAt'>,
  nowMs: number = Date.now(),
): boolean {
  return !isSoftDeleted(doc);
  // NB: the TTL check is intentionally not OR'd in here. An expired
  // soft-delete is STILL a soft-delete — it's just eligible for cron
  // purge. Either way the doc stays hidden until purge lands, which is
  // exactly what we want. The TTL helper is exposed for the admin
  // panel + the purge cron, not the default filter.
}

/**
 * Count of reports on a site that still reference the given document.
 * Surface copy in the confirm modal reads "This document is referenced
 * by {N} reports …". Pure scan over `report.documents` + `documentRefs`
 * — same union the DDL-1 cross-array delete sweeps, so the counts agree
 * with what the delete will actually touch.
 *
 * `null` / undefined arrays default to "no references" instead of
 * throwing, so the helper can run pre-load against partial state.
 */
export function countReferencingReports(
  reports: ReadonlyArray<Pick<SiteRagReport, 'siteId' | 'documents' | 'documentRefs'>>,
  siteId: string,
  docId: string,
): number {
  if (!siteId || !docId) return 0;
  let count = 0;
  for (const r of reports) {
    if (r.siteId !== siteId) continue;
    const inDocs = (r.documents ?? []).some(d => d.id === docId);
    const inRefs = (r.documentRefs ?? []).some(ref => ref.docId === docId);
    if (inDocs || inRefs) count += 1;
  }
  return count;
}
